import type { BaseSyntheticEvent, KeyboardEvent } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type {
  FieldType,
  FormField
} from '@elfsight/widget-form-builder/widget';
import {
  fieldComponents as defaultComponents,
  FieldComponent as Field,
  FormContext,
  useFormSelector,
  useGetCountries
} from '@elfsight/widget-form-builder/widget';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { ActionButton } from '../components/action-button';
import { LinkButton } from '../components/link-button';
import {
  BODY1_TEXT_STYLE,
  BUTTON_TEXT_STYLE,
  TITLE1_TEXT_STYLE,
  Title2
} from '../components/typography';
import { Paragraph } from '../fieldComponents';
import { useGetterInvalidFieldErrorMessage } from '../utils/use-getter-invalid-field-error-message';
import { useWidgetDimensions } from '../widget-dimensions-provider';
import { NOTES_ID } from './fields';

const FOCUSABLE_FIELD_TYPES: FieldType[] = [
  'shortText',
  'longText',
  'email',
  'URL',
  'phone',
  'number'
];

export function FormLayout({
  onSubmit,
  displayedFields
}: {
  formSettings: unknown;
  displayedFields: FormField[];
  onSubmit?(e: BaseSyntheticEvent): Promise<void>;
}) {
  const [error, setError] = useState<string | undefined>(undefined);
  const { accentColor, textColor, language } = useSettings();
  const { status = '', fieldsError } = useFormSelector();
  const getInvalidFieldErrorMessage = useGetterInvalidFieldErrorMessage();
  const { t } = useLingui();
  const defaultErrorText = t`An error occurred when submitting the form, please try again later`;
  const errorRequiredText = t`Please fill all the required fields`;
  const requiredErrorMessage = t`This field is required` as string;
  const { isMobile } = useWidgetDimensions();
  const { countries } = useGetCountries();

  const visibleNonHiddenFields = displayedFields.filter(
    (field) => field.visible && field.type !== 'hidden'
  );

  const notesIndex = visibleNonHiddenFields.findIndex(
    (field) => field.id === NOTES_ID
  );

  const hasNotes = notesIndex !== -1;
  const noteField = visibleNonHiddenFields[notesIndex];
  const isNoteRequired = noteField?.required;
  const hasVisibleFieldsAfterNotes =
    hasNotes && notesIndex < visibleNonHiddenFields.length - 1;
  const isNotesFieldOnly = hasNotes && visibleNonHiddenFields.length === 1;

  const subTextColor = getColorWithAlpha(textColor, 0.5);

  const fieldProps = {
    formSettings: {
      fieldColor: textColor,
      fieldActiveColor: accentColor
    },
    fieldPadding: 0,
    fieldSpacing: 0,
    fieldBorderType: 'outline',
    fieldBorderColor: getColorWithAlpha(textColor, 0.2),
    fieldBorderWidth: 1,
    fieldBorderRadius: 8,
    fieldFillColor: 'transparent',
    fieldLabelPosition: 'inside',
    fieldHintColor: getColorWithAlpha(textColor, 0.7),

    fieldInsideLabelColor: subTextColor,
    fieldLabelColor: textColor,
    fieldLabelFontStyle: BUTTON_TEXT_STYLE,
    errorColor: 'red'
  };

  const componentFieldProps = {
    number: {
      showPlaceholder: true,
      fieldInsideLabelColor: subTextColor
    },
    phone: {
      searchPlaceholderText: t`Search country...`,
      emptyCountryListMessage: t`No countries found`,
      ariaLabelCountryDropdown: t`Country selection list`,
      ariaLabelCountryDropdownTrigger: t`Toggle country selection`,
      countries
    },
    heading: {
      fieldHeadingFontStyle: TITLE1_TEXT_STYLE,
      fieldHintFontStyle: BODY1_TEXT_STYLE
    },
    imageChoice: {
      fieldLabelPosition: 'outside' as const,
      fieldLabelColor: textColor,
      fieldLabelFontStyle: BUTTON_TEXT_STYLE
    },
    dropdown: {
      fieldLabelPosition: 'outside' as const,
      fieldLabelColor: textColor,
      fieldLabelFontStyle: BUTTON_TEXT_STYLE
    },
    file: {
      extensionErrorMessage: t`This file cannot be uploaded because the file format is unsupported`,
      emptyFileErrorMessage: t`This file cannot be uploaded because it is empty`,
      fileSizeExceededErrorMessage: t`This file cannot be uploaded because file size limit (100 MB) is exceeded`,
      unexpectedErrorMessage: t`An unexpected error occurred while uploading the file. Please try again`,
      dropFileText: t`Drop file here`,
      chooseFileText: t`Choose file`,
      orDropFileText: t`or drop file here`
    }
  };

  const {
    state: {
      fields: { notes }
    }
  } = useContext(FormContext);

  const shouldShowNotes =
    isNoteRequired ||
    !!notes?.value ||
    hasVisibleFieldsAfterNotes ||
    isNotesFieldOnly;

  const [withNotes, setWithNotes] = useState(shouldShowNotes);

  const finalFields = displayedFields.filter(
    ({ id }) => id !== NOTES_ID || (id === NOTES_ID && withNotes)
  );

  const focusedFieldRef = useRef<TextControlBaseInstance>(null);
  const [focusedFieldId, setFocusedFieldId] = useState(
    visibleNonHiddenFields.find((field) =>
      FOCUSABLE_FIELD_TYPES.includes(field.type)
    )?.id
  );

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      focusedFieldRef.current?.focusInput?.();
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [focusedFieldId]);

  useEffect(() => {
    setWithNotes(shouldShowNotes);
  }, [
    shouldShowNotes,
    hasVisibleFieldsAfterNotes,
    isNoteRequired,
    notes?.value,
    isNotesFieldOnly
  ]);

  useEffect(() => {
    if (status === 'failed') {
      setError(defaultErrorText);
      return;
    }

    if (fieldsError.required) {
      setError(errorRequiredText);
      return;
    }

    setError(undefined);
  }, [status, fieldsError, defaultErrorText, errorRequiredText]);

  const handleAddNotes = () => {
    setWithNotes(true);
    setFocusedFieldId(NOTES_ID);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && onSubmit) {
      onSubmit(e);
    }
  };

  const fieldComponents = {
    ...defaultComponents,
    paragraph: Paragraph
  };

  return (
    <Container onKeyDown={handleKeyDown}>
      <Title _isMobile={isMobile}>{t`Enter Details`}</Title>
      {finalFields.map((field: FormField) => (
        <Field
          ref={field.id === focusedFieldId ? focusedFieldRef : undefined}
          onFocus={() => setFocusedFieldId(field.id)}
          key={field.id}
          field={field}
          fieldComponents={fieldComponents}
          invalidErrorMessage={getInvalidFieldErrorMessage(field)}
          requiredErrorMessage={requiredErrorMessage}
          componentFieldProps={componentFieldProps}
          widgetLanguage={language?.language}
          {...fieldProps}
        />
      ))}
      {!withNotes && hasNotes && (
        <ActionRow>
          <LinkButton onClick={handleAddNotes}>{t`Add Notes`}</LinkButton>
        </ActionRow>
      )}
      <ActionButton onClick={onSubmit} loading={status === 'pending'}>
        {t`Confirm Booking`}
      </ActionButton>
      {['failed', 'invalid'].includes(status) && error && (
        <ErrorText>{error}</ErrorText>
      )}
    </Container>
  );
}

const ActionRow = styled.div.attrs({
  className: createReadableClass({
    component: 'form-layout',
    element: 'action-row'
  })
})`
  margin-left: auto;
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'form-layout',
    element: 'container'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled(Title2).attrs({
  className: createReadableClass({
    component: 'form-layout',
    element: 'title'
  })
})<{
  _isMobile?: boolean;
}>`
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      font-size: 15px;
    `}
`;

const ErrorText = styled.div`
  font-size: 14px;
  color: #ff4734;
  margin: 0 auto;
`;
